import styled from '@emotion/styled';
import {
  Button,
  Input,
  Modal,
  Space,
  Table,
  TableColumnsType,
  Tooltip,
} from 'antd';
import { orderBy } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaDownload, FaEllipsisH, FaPlus } from 'react-icons/fa';
import { FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PulseLoader } from 'react-spinners';

import { BondEntity } from '../../../../entities/bond.entity';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { DetailBondEntity } from '../../../../entities/detailBond.entity';
import { ROUTES } from '../../../../routes';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { Layout } from '../../../shared/Layout';
import { exportBondsData } from '../components/exportDataExcel';
import { OperationTitreForm } from '../components/OperationTitreForm';
import { getTitres } from '../network';

const Container = styled.div`
  height: calc(100vh - 30px);
  display: flex;
  background-color: #1c2025;
  color: white;

  .bread-crum span {
    color: white;
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: #757575;
    color: white;
  }

  .even-row {
    background-color: #757575;
    color: white;
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: ${YELLOW} !important;
      cursor: pointer;
      color: white;
    }
  }

  > .content {
    padding: 50px;
    width: 100%;
    background-color: #1c2025;
    color: white;
  }
`;

export const EmissionTitrePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [bonds, setBonds] = useState<BondEntity[]>([]);
  const [filterBonds, setFilterBonds] = useState<BondEntity[]>([]);
  const [activeExpRow, setActiveExpRow] = useState<string[]>([]);
  const [selectedDetailBond, setSelectedDetailBond] =
    useState<DetailBondEntity>();
  const [showModal, setShowModal] = useState(false);
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const history = useHistory();

  const fetchData = () => {
    setIsLoading(true);
    getTitres(connectedUser.id).then(({ data }) => {
      setBonds(orderBy(data, 'created_at', 'desc'));
      setFilterBonds(orderBy(data, 'created_at', 'desc'));
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterBonds(
      bonds.filter(
        (bond) =>
          bond.code.toLowerCase().includes(e.target.value.toLowerCase()) ||
          bond.pays.toLowerCase().includes(e.target.value.toLowerCase()) ||
          bond.instrument
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          bond.dateEmission
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          bond.dateMaturite
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          bond.created_at.toLowerCase().includes(e.target.value.toLowerCase()),
      ),
    );
  };

  const EXPANDED_COLUMNS: TableColumnsType<DetailBondEntity> = [
    {
      title: 'Sous Code',
      dataIndex: 'subCode',
    },
    {
      title: 'Détenteur',
      render: (cell: any, row) =>
        `${row.detenteur.code_referent} - ${row.detenteur.nom} ${row.detenteur.prenom}`,
    },
    {
      title: 'Quantité',
      dataIndex: 'qte',
      render: (cell: any) => `${formatNum(cell)}`,
    },
    {
      title: 'Montant',
      dataIndex: 'montant',
      render: (cell: any) => `${formatNum(Number(cell))}`,
    },
    {
      title: 'Prix nominale',
      dataIndex: 'prixNominale',
      render: (cell: any) => `${formatNum(Number(cell))}`,
    },
    {
      title: 'Décote (%)',
      dataIndex: 'decote',
      render: (cell: any) => `${formatNum(Number(cell))}`,
    },
    {
      title: 'Code origin',
      dataIndex: 'originCode',
    },
    {
      title: 'Rendement (%)',
      dataIndex: 'tauxRendement',
      render: (cell: any) => `${formatNum(Number(cell))}`,
    },
    {
      title: "Date d'acquisition",
      dataIndex: 'dateAcquisition',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    },
    {
      title: 'Date attribution',
      dataIndex: 'created_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    },
    {
      title: 'Etat',
      dataIndex: 'etat',
    },
    {
      title: 'Actions',
      render: (cell: any, row) => (
        <Space>
          <Button
            icon={<FaEllipsisH style={{ color: 'black', marginBottom: -2 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
              borderColor: PRIMARY,
            }}
            onClick={() => {
              setSelectedDetailBond(row);
              setShowModal(true);
            }}
          />
        </Space>
      ),
      align: 'center' as const,
    },
  ];

  return (
    <Layout>
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <h2 style={{ marginTop: 10, color: 'white' }}>Liste des titres</h2>
          <div style={{ marginTop: 20 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 5,
              }}
            >
              <Space>
                <Input
                  placeholder='Search...'
                  style={{ width: 250 }}
                  onChange={search}
                />
                <Tooltip title='Rafraichir' placement='top' color={YELLOW}>
                  <span style={{ cursor: 'pointer' }} onClick={fetchData}>
                    <FiRefreshCcw color='black' />
                  </span>
                </Tooltip>
                <PulseLoader
                  size={5}
                  margin={2}
                  color={YELLOW}
                  loading={isLoading}
                />
              </Space>

              <Space>
                <Button
                  icon={<FaPlus style={{ marginBottom: -2, marginRight: 5 }} />}
                  className='butt'
                  style={{ borderRadius: 5 }}
                  onClick={() => history.push(ROUTES.PANEL.EMISSION.NEW_BOND)}
                >
                  Nouveau bond
                </Button>
                {/* <Button
                  icon={
                    <FaUpload style={{ marginBottom: -2, marginRight: 5 }} />
                  }
                  className='butt'
                  style={{ borderRadius: 5 }}
                  onClick={() => {}}
                >
                  Importer des bonds
                </Button> */}
                <Button
                  icon={
                    <FaDownload style={{ marginBottom: -2, marginRight: 5 }} />
                  }
                  className='butt'
                  style={{ borderRadius: 5 }}
                  onClick={() => exportBondsData(bonds)}
                >
                  Exporter
                </Button>
              </Space>
            </div>

            <Table
              columns={COLUMNS}
              dataSource={filterBonds}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              size='small'
              loading={isLoading}
              style={{ zoom: 0.9 }}
              rowKey='id'
              pagination={{ pageSize: 20 }}
              expandable={{
                expandedRowRender: (row) => {
                  return (
                    <Table<DetailBondEntity>
                      columns={EXPANDED_COLUMNS}
                      dataSource={row.details}
                      rowKey='id'
                      pagination={false}
                    />
                  );
                },
                expandedRowKeys: activeExpRow,
                onExpand: (expanded, row) => {
                  const keys: string[] = [];
                  if (expanded) {
                    keys.push(row.id);
                  }
                  setActiveExpRow(keys);
                },
              }}
              // pagination={false}
            />
          </div>
        </div>
        <Modal
          open={showModal}
          title={
            <p style={{ textAlign: 'center', margin: 0 }}>
              Effectuer des opérations sur le titre
            </p>
          }
          onCancel={() => setShowModal(false)}
          footer={null}
        >
          <OperationTitreForm
            setShowModal={setShowModal}
            detailBond={selectedDetailBond}
          />
        </Modal>
      </Container>
    </Layout>
  );
};

const COLUMNS = [
  {
    title: 'CODE',
    dataIndex: 'code',
  },
  {
    title: 'Emetteur',
    dataIndex: 'pays',
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
  },
  {
    title: "Date d'emission",
    dataIndex: 'dateEmission',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Durée (mois)',
    dataIndex: 'dureeMaturite',
  },
  {
    title: 'Coupon (%)',
    dataIndex: 'tauxMarginal',
    render: (cell: any) => `${formatNum(Number(cell))}`,
  },
  {
    title: 'Fréquence',
    dataIndex: 'frequencePaiement',
  },
  {
    title: "Date d'ajout",
    dataIndex: 'created_at',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
];
